<template>
  <div class="card_flex" id="overview">
    <div class="titleInfo">Overview</div>
    <div class="card_list">
      <div class="item" v-for="(item, index) in cardList" :key="index">
        <img class="iconImg" :src="require(`../../assets/images/${item.iconImg}`)" alt="" />
        <div class="title">{{ item.title }}</div>
        <div class="text" v-html="item.text"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "OverviewSection",
  data() {
    return {
      cardList: [
        {
          iconImg: "icon4.png",
          title: "Our Task: Needle in a Montage",
          text: "We introduce a novel task of Needle in a Montage (NeMo), which is designed to assess the critical video reasoning capabilities of Video Large Language Models (VideoLLMs), including long-context recall and temporal grounding. Specifically, our task embeds one or more needles (i.e., short video clips) within a montage (i.e., a long video) composed from many loosely related clips, where models must retrieve each individual needle by identifying its temporal extent.",
        },
        {
          iconImg: "icon2.png",
          title: "Our Automatic Data Generation Pipeline",
          text: "To generate video question answering data for our NeMo task, we first propose a comprehensive yet compact video representation, which follows a hierarchical structure enriched with semantic features, incorporating scene-level and object-level features. We then develop a scalable automatic data generation pipeline that effectively leverages the extracted video representations to facilitate high-quality data synthesis. Please refer to our technical report for more details.",
        },
        {
          iconImg: "icon1.png",
          title: "Our Benchmark: NeMoBench",
          text: "Built upon the proposed automatic pipeline, we present NeMoBench, a video-language benchmark centered on our NeMo task. Specifically, our benchmark includes two key components: (1) NeMoBench-Full: a comprehensive set comprising 31,378 automatically generated QA pairs about objects and scenes from 13,486 videos of varying durations, and (2) NeMoBench-Clean: a core subset containing 2,053 manually verified QA pairs from 950 videos, with durations ranging from seconds to hours.",
        },
        {
          iconImg: "icon3.png",
          title: "Experimental Results",
          text: "Our experimental results demonstrate that the proposed task presents substantial challenges for both open-source and closed-source models, with a significant performance gap compared to human benchmarks across various montage durations. Moreover, we observe that model performance rankings remain consistent across both NeMoBench-Clean and NeMoBench-Full, demonstrating that our pipeline can reliably and automatically generate high-quality evaluation data. ",
        },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
$tablet: 1024px; // 平板及以下（≤1024px）
$mobile: 768px; // 移动端及以下（≤768px）

.card_flex {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 40px;
  .titleInfo {
    font-family: PingFangSC, PingFang SC;
    font-weight: 600;
    color: #ffffff;
    text-align: center;
    font-style: normal;
  }

  .card_list {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 20px;
    width: 100%;
    max-width: 1200px;
    height: auto;

    .item {
      width: 100%;
      padding: 20px;
      background: rgba(27, 34, 46, 0.5);
      border-radius: 18px;
      display: flex;
      flex-direction: column;
      align-items: center;

      .iconImg {
        margin-top: 20px;
        width: 100px;
        height: 95px;
        animation: bounceFall 3s infinite;
        flex-shrink: 0;
      }

      .title {
        font-family: PingFangSC, PingFang SC;
        font-weight: 600;
        font-size: 18px;
        color: #ffffff;
        line-height: 28px;
        text-align: center;
        font-style: normal;
        margin-top: 14px;
        flex-shrink: 0;
      }

      .text {
        font-family: PingFangSC, PingFang SC;
        font-weight: 400;
        font-size: 14px;
        color: #a9abae;
        line-height: 24px;
        text-align: left;
        font-style: normal;
        margin-top: 17px;
        width: 100%;
        height: auto;
        display: block;
        -webkit-line-clamp: unset;
        overflow: visible;
      }
    }
  }
}

// 平板适配（≤1024px）
@media (min-width: $mobile + 1) and (max-width: $tablet) {
  .card_flex {
    .titleInfo {
      font-size: 36px;
      margin-bottom: 25px;
    }
    .card_list {
      grid-template-columns: repeat(2, 1fr);
    }

    .item .text {
      font-size: 18px;
      line-height: 28px;
    }
  }
}

// 移动端适配（≤768px）
@media (max-width: $mobile) {
  .card_flex {
    .titleInfo {
      font-size: 26px;
      margin-bottom: 20px;
      height: auto;
    }

    .card_list {
      grid-template-columns: 1fr;
    }

    .item .text {
      font-size: 14px;
      line-height: 22px;
    }
  }
}

@keyframes bounceFall {
  0% {
    transform: translateY(-300px);
    opacity: 0;
    animation-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  12% {
    transform: translateY(0);
    opacity: 1;
    animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
  }
  18% {
    transform: translateY(-20px);
    animation-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  22% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
  }
  26% {
    transform: translateY(-15px);
    animation-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  29% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
  }
  32% {
    transform: translateY(-10px);
    animation-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  34% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
  }
  36% {
    transform: translateY(-6px);
    animation-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  38% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
  }
  40% {
    transform: translateY(-3px);
    animation-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  42% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
  }
  100% {
    transform: translateY(0);
  }
}
</style>
